import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { useState } from "react";
import {
  Role,
  User,
} from "../../services/api/__generated__/backend_gateway-types";

// F-03: the invitable roles. NONE is a schema value for other purposes (e.g.
// a customer's unset defaultRole) and is never a valid invite target — the
// backend rejects it too. The backend is the authority on which of these a
// given caller may actually grant (an ADMIN caller inviting ROOT is refused
// server-side); this dropdown doesn't try to pre-filter by the viewer's own
// role.
const INVITABLE_ROLES: Role[] = [Role.Admin, Role.Root];

interface MyTeamComponentProps {
  teamMembers: User[];
  loading: boolean;
  error: any;
  inviteModalOpen: boolean;
  removeConfirmModalOpen: boolean;
  userToRemove: string | null;
  inviteLoading: boolean;
  removeLoading: boolean;
  passwordLoading: boolean;
  onInviteTeamMember: (
    email: string,
    role: Role,
    temporaryPassword?: string
  ) => Promise<void>;
  onRemoveTeamMember: (email: string) => void;
  onConfirmRemoveTeamMember: () => Promise<void>;
  onSetPassword: (email: string, temporaryPassword: string) => Promise<void>;
  onOpenInviteModal: () => void;
  onCloseInviteModal: () => void;
  onCloseRemoveConfirmModal: () => void;
}

interface ExpandedRowState {
  [key: string]: boolean;
}

export const MyTeamComponent = ({
  teamMembers,
  loading,
  error,
  inviteModalOpen,
  removeConfirmModalOpen,
  userToRemove,
  inviteLoading,
  removeLoading,
  passwordLoading,
  onInviteTeamMember,
  onRemoveTeamMember,
  onConfirmRemoveTeamMember,
  onSetPassword,
  onOpenInviteModal,
  onCloseInviteModal,
  onCloseRemoveConfirmModal,
}: MyTeamComponentProps) => {
  const [expandedRows, setExpandedRows] = useState<ExpandedRowState>({});
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePassword, setInvitePassword] = useState("");
  // F-03: no preselected role — "" is the unselected state, distinct from
  // any real Role value, and Send stays disabled until the caller picks one.
  const [inviteRole, setInviteRole] = useState<Role | "">("");
  const [passwordInputs, setPasswordInputs] = useState<{
    [key: string]: string;
  }>({});

  const handleToggleExpanded = (userId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handleInviteSubmit = async () => {
    if (inviteEmail.trim() && inviteRole) {
      await onInviteTeamMember(
        inviteEmail.trim(),
        inviteRole,
        invitePassword.trim() || undefined
      );
      setInviteEmail("");
      setInvitePassword("");
      setInviteRole("");
    }
  };

  const handleSetPassword = async (email: string) => {
    const password = passwordInputs[email];
    if (password?.trim()) {
      await onSetPassword(email, password.trim());
      setPasswordInputs((prev) => ({
        ...prev,
        [email]: "",
      }));
    }
  };

  const handlePasswordInputChange = (email: string, value: string) => {
    setPasswordInputs((prev) => ({
      ...prev,
      [email]: value,
    }));
  };

  const getStatusDisplayText = (status: string | null | undefined) => {
    switch (status?.toUpperCase()) {
      case "CONFIRMED":
      case "EXTERNAL_PROVIDER":
        return "Active";
      case "FORCE_CHANGE_PASSWORD":
      case "RESET_REQUIRED":
        return "Change password";
      case "UNCONFIRMED":
        return "Validate email";
      case "ARCHIVED":
      case "COMPROMISED":
      case "UNKNOWN":
        return "Ask Atomic";
      default:
        return status || "N/A";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load team members. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h1">My Team</Typography>
        <Button
          variant="outlined"
          startIcon={<PersonAddIcon />}
          onClick={onOpenInviteModal}
        >
          Invite Team Member
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamMembers.map((member) => (
              <>
                <TableRow key={member.id}>
                  <TableCell>
                    <IconButton
                      onClick={() => handleToggleExpanded(member.id || "")}
                      size="small"
                    >
                      {expandedRows[member.id || ""] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.role || "N/A"}</TableCell>
                  <TableCell>{getStatusDisplayText(member.status)}</TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => onRemoveTeamMember(member.email || "")}
                      color="error"
                      size="small"
                      disabled={removeLoading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={5}
                  >
                    <Collapse
                      in={expandedRows[member.id || ""]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ margin: 2 }}>
                        <Typography
                          variant="subtitle1"
                          gutterBottom
                          component="div"
                        >
                          Set New Password
                        </Typography>
                        <Box display="flex" gap={2} alignItems="center" mb={2}>
                          <TextField
                            label="New Password"
                            type="password"
                            value={passwordInputs[member.email || ""] || ""}
                            onChange={(e) =>
                              handlePasswordInputChange(
                                member.email || "",
                                e.target.value
                              )
                            }
                            size="small"
                            sx={{ minWidth: 200 }}
                          />
                          <Button
                            variant="outlined"
                            startIcon={<LockResetIcon />}
                            onClick={() =>
                              handleSetPassword(member.email || "")
                            }
                            disabled={
                              passwordLoading ||
                              !passwordInputs[member.email || ""]?.trim()
                            }
                            size="small"
                          >
                            Set Password
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite Team Member Modal */}
      <Dialog
        open={inviteModalOpen}
        onClose={onCloseInviteModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="invite-role-label">Role</InputLabel>
            <Select
              labelId="invite-role-label"
              label="Role"
              value={inviteRole}
              displayEmpty
              onChange={(e) => setInviteRole(e.target.value as Role | "")}
            >
              {INVITABLE_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Temporary Password (Optional)"
            type="password"
            fullWidth
            variant="outlined"
            value={invitePassword}
            onChange={(e) => setInvitePassword(e.target.value)}
            helperText="If not provided, user will need to set their own password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseInviteModal} disabled={inviteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            disabled={inviteLoading || !inviteEmail.trim() || !inviteRole}
          >
            {inviteLoading ? <CircularProgress size={20} /> : "Invite"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Team Member Confirmation Modal */}
      <Dialog open={removeConfirmModalOpen} onClose={onCloseRemoveConfirmModal}>
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {userToRemove} from your team? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseRemoveConfirmModal} disabled={removeLoading}>
            Cancel
          </Button>
          <Button
            onClick={onConfirmRemoveTeamMember}
            color="error"
            variant="contained"
            disabled={removeLoading}
          >
            {removeLoading ? <CircularProgress size={20} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
